import React, { useEffect, useState, useRef } from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { useToken } from "../../hooks/token/useToken";

function getMinutes(durationStr) {
  if (!durationStr) return 0;
  const match = durationStr.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

function getTimeRange(startTime12h, durationMinutes) {
  if (!startTime12h) return null;
  try {
    const match = startTime12h.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
    if (!match) return null;
    let [_, hour, minute, period] = match;
    hour = parseInt(hour, 10);
    minute = parseInt(minute, 10);
    period = period.toLowerCase();
    if (period === "pm" && hour !== 12) hour += 12;
    if (period === "am" && hour === 12) hour = 0;
    const startDate = new Date();
    startDate.setHours(hour, minute, 0, 0);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    const formatTime = (date) => {
      const h = date.getHours().toString().padStart(2, "0");
      const m = date.getMinutes().toString().padStart(2, "0");
      return `${h}:${m}`;
    };
    return `${formatTime(startDate)}-${formatTime(endDate)}`;
  } catch (error) {
    console.error("Error calculating time range:", error);
    return null;
  }
}

const StaffSelector = ({
  appointment,
  mode,
  employees,
  index,
  handleAppointmentFieldChange,
  excludeIds = new Set(),
}) => {
  const { isEmployee, decodedData } = useToken();
  const [availableStaffs, setAvailableStaffs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [noStaffAvailable, setNoStaffAvailable] = useState(false);
  const prevValuesRef = useRef({
    start_date: null,
    start_time: null,
    duration: null,
  });

  useEffect(() => {
    const getAvailableVendorStaffs = async () => {
      if (mode === "update") {
        try {
          const currentValues = {
            start_date: appointment.start_date,
            start_time: appointment.start_time,
            duration: appointment.duration,
          };
          const prevValues = prevValuesRef.current;
          const valuesChanged =
            prevValues.start_date !== currentValues.start_date ||
            prevValues.start_time !== currentValues.start_time ||
            prevValues.duration !== currentValues.duration;

          if (!valuesChanged && availableStaffs.length > 0) {
            return;
          }

          prevValuesRef.current = currentValues;

          if (
            !appointment.start_date ||
            !appointment.start_time ||
            !appointment.duration
          ) {
            setAvailableStaffs(employees);
            setNoStaffAvailable(false);
             setHasFetched(true);
            return;
          }

          setIsLoading(true);
          const timeSlots = getTimeRange(
            appointment.start_time,
            getMinutes(appointment.duration)
          );

          if (!timeSlots) {
            setAvailableStaffs(employees);
             setHasFetched(true);
             setNoStaffAvailable(false);
            setIsLoading(false);
            return;
          }

          const payload = {
            date: appointment.start_date,
            timeSlots: timeSlots,
            serviceCategoryTypeId: appointment.service_category_type_id,
            appointmentId: appointment.ref_id || null,
          };

          const response = await httpService.post(
            "/employees/getAvailableVendorEmployees",
            payload,
          );

          const filteredStaffs = response.data.filter(
            (item) => item.is_available === true || item.is_available == null,
          );

          setAvailableStaffs(filteredStaffs);
          setHasFetched(true);
          setNoStaffAvailable(filteredStaffs.length === 0);
          if (isEmployee) {
            const employeeId = decodedData?.data?.user?.employee_id;
            const isEmployeeFree = filteredStaffs.some(
              (item) => item._id === employeeId,
            );
            if (!isEmployeeFree)
              toast.warn("You are not available for the selected time slot!!");
          }
        } catch (error) {
          console.error("Error checking staff occupancy:", error);
          setAvailableStaffs(employees);
          setNoStaffAvailable(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setAvailableStaffs(employees);
      }
    };

    const timer = setTimeout(() => {
      getAvailableVendorStaffs();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    mode,
    appointment.start_date,
    appointment.start_time,
    appointment.duration,
    appointment.ref_id,
    appointment.service_category_type_id,
    employees,
    isEmployee,
    decodedData,
  ]);

  const staffToDisplay =
    //availableStaffs.length > 0 ? availableStaffs : employees;
       hasFetched ? availableStaffs : employees;
  const filteredStaff = appointment.service_category_type_id
    ? staffToDisplay.filter((emp) => {
        const hasMatchingCategory = emp.service_category?.some(
          (sc) =>
            sc.service_category_type_id ===
            appointment.service_category_type_id,
        );
        return hasMatchingCategory;
      })
    : staffToDisplay;

  const displayStaff = filteredStaff.filter((emp) => !excludeIds.has(emp._id));

  // ✅ Handle employee auto-selection in useEffect to avoid infinite loop
  useEffect(() => {
    if (isEmployee && displayStaff.length > 0) {
      const empId = decodedData?.data?.user?.employee_id;
      if (empId) {
        const emp = displayStaff.find((emp) => emp._id === empId);
        // Only call if not already set
        if (appointment.technician_id !== empId) {
          handleAppointmentFieldChange(index, "technician_id", empId);
          handleAppointmentFieldChange(index, "employee_name", emp?.name || "");
        }
      }
    }
  }, [
    isEmployee,
    displayStaff,
    decodedData,
    appointment.technician_id,
    index,
    handleAppointmentFieldChange,
  ]);

  // ✅ Show read-only for employees
  if (isEmployee) {
    const empId = decodedData?.data?.user?.employee_id;
    const emp = displayStaff.find((emp) => emp._id === empId);
    return (
      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm">
        {emp?.name || "Loading..."}
      </div>
    );
  }

  return (
  <div>
    <select
      value={appointment.technician_id || ""}
      onChange={(e) => {
        const empId = e.target.value;
        const emp = displayStaff.find((emp) => emp._id === empId);

        handleAppointmentFieldChange(index, "technician_id", empId);
        handleAppointmentFieldChange(
          index,
          "employee_name",
          emp?.name || ""
        );
      }}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
      disabled={isLoading}
    >
      <option value="">Select staff</option>

      {isLoading ? (
        <option value="" disabled>
          Loading available staff...
        </option>
      ) : (
        displayStaff.map((emp) => (
          <option key={emp._id} value={emp._id}>
            {emp.name}
          </option>
        ))
      )}
    </select>

    {/* No staff available message */}
    {noStaffAvailable && !isLoading && (
      <div
        style={{
          marginTop: "4px",
          fontSize: "11px",
          color: "#dc2626",
          fontWeight: 500,
        }}
      >
        No staff is available for the selected time.
      </div>
    )}
  </div>
);
};

export default StaffSelector;